import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { DonorAuthProvider } from './auth/DonorAuthContext';
import DonorAuthGuard from './auth/DonorAuthGuard';
import { donorDomainUrlFor, portalPath, shouldMoveToDonorDomain } from './portalPaths';
import './pages/DonorPortal.css';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));

const Loading = () => (
  <div className="dp-auth-shell">
    <div className="dp-auth-card" role="status" aria-live="polite">
      <p className="dp-auth-sub mb-0">Loading...</p>
    </div>
  </div>
);

/**
 * /donor-portal on the Donation Portal's domain, once donors have a domain of
 * their own: send them to the same page there. replace() keeps the old address
 * out of history, so Back does not bounce them here again.
 */
const MoveToDonorDomain = () => {
  const { pathname, search, hash } = useLocation();
  const target = donorDomainUrlFor(pathname, search, hash);

  useEffect(() => {
    if (target) window.location.replace(target);
  }, [target]);

  return <Loading />;
};

/**
 * The Donor Portal's route tree.
 *
 * Mounted in App.js either at the root of the donor domain or under
 * /donor-portal everywhere else (see portalPaths.js). Relative paths below work
 * under both.
 *
 * Mounted as a top-level sibling, deliberately outside MainLayout,
 * outside AuthGuard and outside the admin app's AuthContext. A donor is not a
 * masjid administrator, and nothing about the admin session should be able to
 * grant or deny access here -- the mockup was originally registered inside
 * MainRouteList with role: 2, which required an admin login and rendered the
 * admin sidebar around a donor-facing page.
 *
 * DonorAuthProvider wraps only this subtree, so donor session state never leaks
 * into the rest of the application.
 */
export default function DonorPortalRoutes() {
  if (shouldMoveToDonorDomain()) {
    return <MoveToDonorDomain />;
  }

  return (
    <DonorAuthProvider>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="login" element={<LoginPage />} />
          {/* The Hosted UI redirects here. Must match an allowlisted callback URL. */}
          <Route path="auth/callback" element={<AuthCallbackPage />} />
          <Route
            index
            element={
              <DonorAuthGuard>
                <DashboardPage />
              </DonorAuthGuard>
            }
          />
          <Route path="*" element={<Navigate to={portalPath()} replace />} />
        </Routes>
      </Suspense>
    </DonorAuthProvider>
  );
}
